import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { FacetService } from '@core/facet/facet.service';
import { TranslocoService } from '@ngneat/transloco';
import { PageEvent } from '@targx/components/pagination/pagination.definition';
import { environment } from 'environments/environment';
import { combineLatest, map, Observable, tap } from 'rxjs';
import { AutomobiliaSeoService } from '../automobilia-seo.service';
import { AutomobiliaService } from '../automobilia.service';

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
})
export class ListComponent implements OnInit {
  title = '';
  commigSoon = environment.enableMarketplace;
  filtersOpen = false;

  page: PageEvent = {
    pageIndex: 1,
    pageSize: 24,
    length: 0,
  };

  searchQuery = '';
  searchFilters: Data = [];
  facetSelectedEvent: Data = [];

  searchSort = 'published_at_dt desc';

  public translocoSevice = inject(TranslocoService);
  public activeLang: string = this.translocoSevice.getActiveLang();

  constructor(
    private service: AutomobiliaService,
    private route: ActivatedRoute,
    private facets: FacetService,
    private routes: Router,
    private seoService: AutomobiliaSeoService,
  ) {}

  get items$(): Observable<Data> {
    return this.service.products$.pipe(tap((data) => {}));
  }

  get page$(): Observable<Data> {
    return this.service.mktHome$;
  }

  get facets$(): Observable<Data> {
    return this.facets.facets$.pipe(
      map((data) => {
        return data['products'];
      }),
    );
  }

  ngOnInit(): void {
    this.searchQuery = this.route.snapshot.queryParams?.s || '';
    this.page.pageIndex = this.route.snapshot.queryParams?.page || 1;
    this.findProducts();

    combineLatest([this.service.onProductsChanged, this.page$]).subscribe(
      ([products, page]) => {
        this.seoService.setMarketplace({ products, page });
      },
    );
  }

  toggleFilters() {
    this.filtersOpen = !this.filtersOpen;
  }

  /*----------------------------------------------------------------------
   | Handle the page event
   |----------------------------------------------------------------------
   | 1. Set the page
   | 2. Find the products
   |----------------------------------------------------------------------
   */
  resetPage() {
    this.page = {
      pageIndex: 1,
      pageSize: 24,
      length: 0,
    };
  }

  onSearch(search: string) {
    this.resetPage();
    this.searchQuery = search;
    this.findProducts();

    this.routes.navigate(['./'], {
      queryParams: { s: this.searchQuery },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    });
  }

  handlePageEvent(e: PageEvent) {
    this.page = e;
    this.routes.navigate(['./'], {
      queryParams: { s: this.searchQuery, page: e.pageIndex },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    });
    this.findProducts();
  }

  handleFilterEvent(e: Data) {
    const innerFacets = this.facets.facets['products'];
    const innerProcessedFacets = [];
    this.searchFilters = e;
    this.facetSelectedEvent = e;

    /**
     * limpar array dos vazios;
     */
    innerFacets.filterField.forEach((facet) => {
      if (this.searchFilters[facet.solrFilterKey]) {
        if (
          facet.fieldType === 'range' &&
          this.searchFilters[facet.solrFilterKey][facet.solrFilterKey + '_Min']
        ) {
          const range = [0, Infinity];
          if (
            this.searchFilters[facet.solrFilterKey][
              facet.solrFilterKey + '_Min'
            ]
          ) {
            range[0] =
              this.searchFilters[facet.solrFilterKey][
                facet.solrFilterKey + '_Min'
              ];
          }
          if (
            this.searchFilters[facet.solrFilterKey][
              facet.solrFilterKey + '_Max'
            ]
          ) {
            range[1] =
              this.searchFilters[facet.solrFilterKey][
                facet.solrFilterKey + '_Max'
              ];
          }

          innerProcessedFacets.push({
            name: facet.Label,
            key: facet.solrFilterKey,
            values: range,
            fq: facet.solrFilterKey + ':[' + range.join(' TO ') + ']',
          });
        } else {
          if (this.searchFilters[facet.solrFilterKey].length > 0) {
            const OR = ` OR ${facet.solrFilterKey}:`;
            innerProcessedFacets.push({
              name: facet.Label,
              key: facet.solrFilterKey,
              values: this.searchFilters[facet.solrFilterKey],
              fq: `${facet.solrFilterKey}:${this.searchFilters[facet.solrFilterKey].join(OR)}`,
            });
          }
        }
      }
    });

    this.resetPage();

    this.searchFilters = innerProcessedFacets;
    this.findProducts();
  }

  /**
   * 1. Get the category from the route
   * 2. Get the products from the service
   * 3. Get the facets from the service
   * 4. Handle the page event
   * 5. Handle the filter event
   * 6. Handle the sort event
   * 7. Handle the search event
   * 9. Get the products from the service and filter them
   */

  findProducts() {
    const category = this.route.snapshot.paramMap.get('category') || '';

    this.service
      .getSolr(
        'name_s',
        this.searchQuery,
        this.page.pageIndex - 1,
        this.page.pageSize,
        this.searchFilters.map((filter) => filter.fq),
        this.searchSort,
      )
      .subscribe();
  }
}
